import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categoryNameMap } from '@/mocks/data';
import { getArticleSchema, getBreadcrumbSchema, getWebPageSchema } from '@/lib/seo';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-file-search-line text-2xl text-foreground-300"></i>
          </div>
          <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">该文章可能已被移除或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = categoryNameMap[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <div className="pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getArticleSchema(
              article.title,
              article.excerpt,
              `https://anidndd.cn/article/${article.id}`,
              article.date,
              article.author,
              article.image
            )
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbSchema([
              { name: '首页', url: 'https://anidndd.cn/' },
              { name: categoryName, url: `https://anidndd.cn/category/${article.category}` },
              { name: article.title, url: `https://anidndd.cn/article/${article.id}` },
            ])
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getWebPageSchema(
              `${article.title} | 国产精品精选推荐`,
              article.excerpt,
              `https://anidndd.cn/article/${article.id}`
            )
          ),
        }}
      />

      <article>
        {/* Article Header */}
        <header className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/30"></div>
          <div className="absolute bottom-0 left-0 right-0 z-10 p-6 md:p-12">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block bg-primary-500 text-background-50 text-xs font-medium px-2.5 py-1 rounded-full mb-3 whitespace-nowrap">
                {categoryName}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-3 text-sm text-background-200">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="prose prose-lg max-w-none">
            {article.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-foreground-700 leading-relaxed mb-5 text-base">
                {paragraph.trim()}
              </p>
            ))}
          </div>

          {/* Author Card */}
          <div className="mt-10 p-5 bg-background-100 rounded-lg border border-background-200/70 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-3-line text-xl text-secondary-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
              <p className="text-xs text-foreground-500">国产精品 品质编辑</p>
            </div>
          </div>

          {/* Share */}
          <div className="mt-6 flex items-center gap-3">
            <span className="text-sm text-foreground-500">分享到：</span>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 transition-colors cursor-pointer" aria-label="分享到微信">
              <i className="ri-wechat-line text-foreground-600"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 transition-colors cursor-pointer" aria-label="分享到微博">
              <i className="ri-weibo-line text-foreground-600"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 transition-colors cursor-pointer" aria-label="复制链接">
              <i className="ri-link-m text-foreground-600"></i>
            </button>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((ra) => (
                <ArticleCard
                  key={ra.id}
                  {...ra}
                  categoryName={categoryNameMap[ra.category]}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}